import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../lib/api";
import { formatPoints } from "../lib/format";
import type { Card, Person, TransferCapScheme, TransferPartner } from "../lib/types";
import { EmptyState, Modal, Panel } from "./ui";

const PERIODS = [
  { key: "YEAR", label: "per calendar year" },
  { key: "ANNIVERSARY_YEAR", label: "per card year" },
  { key: "QUARTER", label: "per quarter" },
  { key: "MONTH", label: "per month" },
  { key: "LIFETIME", label: "lifetime" },
];
const periodLabel = (key: string) => PERIODS.find((p) => p.key === key)?.label ?? key;

/**
 * What the issuer's own terms say, as a starting point rather than an answer.
 *
 * The caps are filled in; the partner lists deliberately are not. A group with
 * the wrong partners in it silently mis-plans every transfer, and which
 * partner sits in which group is exactly the part issuers move around — so the
 * groups arrive empty and visibly unfinished instead.
 */
const PRESETS = [
  {
    label: "Axis EDGE Miles — Atlas",
    scheme: { name: "Axis EDGE Miles", period: "YEAR", overallPoints: 150000 },
    groups: [
      { name: "Group A", capPoints: 30000 },
      { name: "Group B", capPoints: 120000 },
    ],
  },
  {
    label: "Axis EDGE Rewards — Magnus",
    scheme: { name: "Axis EDGE Rewards", period: "YEAR", overallPoints: 500000 },
    groups: [
      { name: "Group A", capPoints: 100000 },
      { name: "Group B", capPoints: 400000 },
    ],
  },
];

export function TransferCapSchemes({
  cards,
  partners,
  onError,
  onNotice,
}: {
  cards: Card[];
  partners: TransferPartner[];
  onError: (m: string) => void;
  onNotice: (m: string) => void;
}) {
  const [schemes, setSchemes] = useState<TransferCapScheme[] | null>(null);
  const [people, setPeople] = useState<Person[]>([]);
  const [modal, setModal] = useState<{ open: boolean; scheme: TransferCapScheme | null }>({ open: false, scheme: null });

  const load = useCallback(async () => {
    try {
      const [s, p] = await Promise.all([
        api.get<{ schemes: TransferCapScheme[] }>("/transfers/cap-schemes"),
        api.get<{ people: Person[] }>("/people").catch(() => ({ people: [] as Person[] })),
      ]);
      setSchemes(s.schemes);
      setPeople(p.people);
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not load shared allowances");
    }
  }, [onError]);

  useEffect(() => { void load(); }, [load]);

  async function remove(scheme: TransferCapScheme) {
    if (!confirm(`Delete "${scheme.name}"? The cards on it go back to their own caps.`)) return;
    try {
      await api.del(`/transfers/cap-schemes/${scheme.id}`);
      onNotice(`Deleted "${scheme.name}".`);
      await load();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not delete it");
    }
  }

  return (
    <>
      <Panel
        title="Transfer allowances"
        subtitle="A ceiling with capped partner groups inside it, applied to the cards you choose — the shape Axis uses for Group A and Group B."
        actions={
          <button className="btn-secondary px-3 py-1.5 text-xs" onClick={() => setModal({ open: true, scheme: null })}>
            Add an allowance
          </button>
        }
      >
        {schemes == null ? null : schemes.length === 0 ? (
          <EmptyState
            title="No allowances set up"
            hint="Axis caps EDGE transfers by partner group — so much to Group A, so much to Group B, within an overall ceiling for the year. A cap on the whole card constrains partners outside the group; a cap per partner lets three partners spend three times the group's limit. Neither says what the terms say."
            action={
              <button className="btn-primary" onClick={() => setModal({ open: true, scheme: null })}>
                Set one up
              </button>
            }
          />
        ) : (
          <ul className="divide-y divide-ink-100">
            {schemes.map((s) => (
              <li key={s.id} className="py-4 first:pt-0 last:pb-0">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-ink-900">
                      {s.name}
                      {s.person && <span className="ml-2 text-xs font-normal text-ink-500">{s.person.name}</span>}
                    </p>
                    <p className="mt-0.5 text-xs text-ink-600">
                      {s.overallPoints != null
                        ? `Up to ${formatPoints(s.overallPoints)} points ${periodLabel(s.period)}`
                        : `No overall ceiling · groups capped ${periodLabel(s.period)}`}
                      {" · "}
                      {s.cards.length === 0
                        ? "no cards on it yet"
                        : s.cards.map((c) => c.name).join(", ")}
                      {s.cards.length > 1 &&
                        (s.sharedAcrossCards ? " · sharing one pot" : " · each counted separately")}
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-3">
                    <button className="text-xs text-accent-700 underline" onClick={() => setModal({ open: true, scheme: s })}>
                      Edit
                    </button>
                    <button className="text-xs text-red-700 underline" onClick={() => void remove(s)}>
                      Delete
                    </button>
                  </div>
                </div>

                {s.groups.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {s.groups.map((g) => (
                      <li key={g.id} className="flex flex-wrap items-baseline justify-between gap-2 text-xs">
                        <span className="text-ink-700">
                          {g.name}
                          <span className="ml-2 text-ink-500">
                            {g.partners.length === 0
                              ? "no partners assigned — caps nothing yet"
                              : g.partners.map((p) => p.name).join(", ")}
                          </span>
                        </span>
                        <span className="tnum shrink-0 text-ink-600">{formatPoints(g.capPoints)} pts</span>
                      </li>
                    ))}
                  </ul>
                )}

                {s.cards.length === 0 && (
                  <p className="mt-2 text-xs text-amber-700">
                    No card draws on this allowance, so nothing is capped by it yet.
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <SchemeModal
        state={modal}
        cards={cards}
        partners={partners}
        people={people}
        onClose={() => setModal({ open: false, scheme: null })}
        onSaved={() => { setModal({ open: false, scheme: null }); void load(); }}
        onError={onError}
      />
    </>
  );
}

interface GroupDraft {
  id?: string;
  name: string;
  capPoints: string;
  partnerIds: string[];
}

function SchemeModal({
  state,
  cards,
  partners,
  people,
  onClose,
  onSaved,
  onError,
}: {
  state: { open: boolean; scheme: TransferCapScheme | null };
  cards: Card[];
  partners: TransferPartner[];
  people: Person[];
  onClose: () => void;
  onSaved: () => void;
  onError: (m: string) => void;
}) {
  const [name, setName] = useState("");
  const [personId, setPersonId] = useState("");
  const [period, setPeriod] = useState("YEAR");
  const [overall, setOverall] = useState("");
  const [cardIds, setCardIds] = useState<string[]>([]);
  const [shared, setShared] = useState(false);
  const [groups, setGroups] = useState<GroupDraft[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const s = state.scheme;
    setName(s?.name ?? "");
    setPersonId(s?.personId ?? "");
    setPeriod(s?.period ?? "YEAR");
    setOverall(s?.overallPoints != null ? String(s.overallPoints) : "");
    setCardIds(s?.cards.map((c) => c.id) ?? []);
    setShared(s?.sharedAcrossCards ?? false);
    setGroups(
      s?.groups.map((g) => ({
        id: g.id,
        name: g.name,
        capPoints: String(g.capPoints),
        partnerIds: g.partners.map((p) => p.id),
      })) ?? [],
    );
  }, [state.scheme, state.open]);

  function applyPreset(preset: (typeof PRESETS)[number]) {
    setName(preset.scheme.name);
    setPeriod(preset.scheme.period);
    setOverall(String(preset.scheme.overallPoints));
    setGroups(preset.groups.map((g) => ({ name: g.name, capPoints: String(g.capPoints), partnerIds: [] })));
  }

  async function save() {
    setBusy(true);
    try {
      const payload = {
        name: name.trim(),
        personId: personId || null,
        period,
        overallPoints: overall ? Number(overall) : null,
        sharedAcrossCards: shared,
        cardIds,
      };
      const saved = state.scheme
        ? await api.put<{ scheme: TransferCapScheme }>(`/transfers/cap-schemes/${state.scheme.id}`, payload)
        : await api.post<{ scheme: TransferCapScheme }>("/transfers/cap-schemes", payload);
      const schemeId = saved.scheme.id;

      // Groups the scheme had and the form no longer lists are gone.
      const keptIds = new Set(groups.map((g) => g.id).filter(Boolean));
      for (const existing of state.scheme?.groups ?? []) {
        if (!keptIds.has(existing.id)) await api.del(`/transfers/cap-groups/${existing.id}`);
      }
      for (const [index, g] of groups.entries()) {
        const body = {
          name: g.name.trim(),
          capPoints: Number(g.capPoints) || 0,
          sortOrder: index,
          partnerIds: g.partnerIds,
        };
        if (!body.name) continue;
        if (g.id) await api.put(`/transfers/cap-groups/${g.id}`, body);
        else await api.post(`/transfers/cap-schemes/${schemeId}/groups`, body);
      }
      onSaved();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not save the allowance");
    } finally {
      setBusy(false);
    }
  }

  const update = (i: number, patch: Partial<GroupDraft>) =>
    setGroups(groups.map((g, j) => (j === i ? { ...g, ...patch } : g)));

  return (
    <Modal
      open={state.open}
      onClose={onClose}
      title={state.scheme ? "Edit a shared allowance" : "Add a shared allowance"}
      wide
    >
      <div className="space-y-4">
        {!state.scheme && (
          <div className="rounded-lg border border-ink-200 bg-ink-50 p-3">
            <p className="label mb-1">Start from an issuer&apos;s terms</p>
            <p className="mb-2 text-xs text-ink-500">
              Fills in the caps only. Which partner sits in which group is the part issuers change
              most, so the groups arrive empty — assign them from your own card terms.
            </p>
            <div className="flex flex-wrap gap-2">
              {PRESETS.map((p) => (
                <button key={p.label} type="button" className="btn-secondary px-3 py-1.5 text-xs" onClick={() => applyPreset(p)}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="label" htmlFor="cs-name">Name</label>
            <input id="cs-name" className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Axis EDGE Miles" />
          </div>
          <div>
            <label className="label" htmlFor="cs-period">Resets</label>
            <select id="cs-period" className="input" value={period} onChange={(e) => setPeriod(e.target.value)}>
              {PERIODS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="cs-overall">Overall ceiling (points)</label>
            <input id="cs-overall" className="input tnum" type="number" min={0} value={overall} onChange={(e) => setOverall(e.target.value)} placeholder="Leave blank for none" />
          </div>
          {people.length > 0 && (
            <div>
              <label className="label" htmlFor="cs-person">Whose allowance</label>
              <select id="cs-person" className="input" value={personId} onChange={(e) => setPersonId(e.target.value)}>
                <option value="">Mine / not tracked per person</option>
                {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <p className="mt-1 text-xs text-ink-500">
                Axis counts per customer, so each cardholder has their own.
              </p>
            </div>
          )}
        </div>

        <div>
          <label className="label">Cards this applies to</label>
          <div className="flex flex-wrap gap-2">
            {cards.map((c) => {
              const on = cardIds.includes(c.id);
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setCardIds(on ? cardIds.filter((x) => x !== c.id) : [...cardIds, c.id])}
                  className={`rounded-full border px-3 py-1 text-xs ${on ? "border-accent-500 bg-accent-50 text-accent-800" : "border-ink-300 text-ink-600"}`}
                >
                  {c.name}
                </button>
              );
            })}
          </div>
          <label className="mt-3 flex items-start gap-2 text-sm text-ink-700">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-ink-300 text-accent-600 focus:ring-accent-500"
              checked={shared}
              onChange={(e) => setShared(e.target.checked)}
            />
            <span>
              These cards share one pot
              <span className="mt-0.5 block text-xs text-ink-500">
                Off by default: each card gets its own copy of these caps, which is how a cap
                normally reads. Tick it only if your issuer counts across cards — Axis is said to
                count per customer, so two Axis cards would draw on one allowance. Check your own
                terms before relying on it.
              </span>
            </span>
          </label>
        </div>

        <div>
          <label className="label">Capped groups</label>
          <div className="space-y-3">
            {groups.map((g, i) => (
              <div key={i} className="rounded-md bg-ink-50 p-3">
                <div className="flex flex-wrap gap-2">
                  <input
                    className="input w-36"
                    placeholder="Group A"
                    aria-label={`Group ${i + 1} name`}
                    value={g.name}
                    onChange={(e) => update(i, { name: e.target.value })}
                  />
                  <input
                    className="input tnum w-40"
                    type="number"
                    min={0}
                    placeholder="Cap (points)"
                    aria-label={`Group ${i + 1} cap`}
                    value={g.capPoints}
                    onChange={(e) => update(i, { capPoints: e.target.value })}
                  />
                  <button type="button" className="text-xs text-red-700 underline" onClick={() => setGroups(groups.filter((_, j) => j !== i))}>
                    Remove
                  </button>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {partners.map((p) => {
                    const on = g.partnerIds.includes(p.id);
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() =>
                          update(i, {
                            partnerIds: on ? g.partnerIds.filter((x) => x !== p.id) : [...g.partnerIds, p.id],
                          })
                        }
                        className={`rounded-full border px-2.5 py-0.5 text-xs ${on ? "border-accent-500 bg-accent-50 text-accent-800" : "border-ink-300 text-ink-500"}`}
                      >
                        {p.name}
                      </button>
                    );
                  })}
                </div>
                {g.partnerIds.length === 0 && (
                  <p className="mt-2 text-xs text-amber-700">No partners assigned, so this cap does nothing yet.</p>
                )}
              </div>
            ))}
          </div>
          <button
            type="button"
            className="btn-secondary mt-2 text-xs"
            onClick={() => setGroups([...groups, { name: "", capPoints: "", partnerIds: [] }])}
          >
            Add a group
          </button>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={busy || !name.trim()} onClick={() => void save()}>
            {busy ? "Saving…" : "Save allowance"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
